import { fmtDisplayDate } from '@utils/displayDate'
import { readGenericExagoRun } from '@api/v1/exagoReportService'

/**
 * Spec 192: the Exago report as a Report Queue job or schedule.
 *
 * It rides the existing `specialized` job kind: the job stores the page's own run request
 * and cb-api replays it in-process, so every queued run passes the same allowlist and the
 * same CB Exago permission as a click on Run. Two fixed conventions, both checked by cb-api
 * at the door (`exago/jobSpec.js`):
 *
 *   reportId  `exago/<content id>` — so every existing `/reports/${job.reportId}?jobId=`
 *             link (tray, Queue, drawer, notifications, email) opens THIS page.
 *   endpoint  `{ method: 'GET', path: '/reports/exago/<id>/run?asOf=…' }`, nothing else.
 *
 * A plain module (no React) so the page file stays component-only.
 */

/** The job/schedule reportId; lowercase, as cb-api stores and compares it. */
export const exagoJobReportId = (exagoId) => `exago/${String(exagoId).toLowerCase()}`

/** The run route's token for "the day the run executes" (cb-api exago/routes.js). */
export const AS_OF_TODAY = '@today'

/** The viewer's IANA zone; 'UTC' when the browser will not say. */
export const viewerTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/**
 * Does a RECURRING run of these filters ask for the run's own day? Only when the as-of on
 * screen is today: a schedule saved with "today" is a schedule of "as of each run". A
 * chosen past date (a month-end) stays that date on every run. One-off runs (now / later)
 * always send the date on screen.
 */
export const rollsWithRun = ({ asOf } = {}, today) => !!asOf && asOf === today

/** The replay spec a queued run stores — the same request the page's Run makes. */
export function exagoQueueEndpoint(exagoId, { asOf, earliest } = {}, { rolling = false, tz = viewerTimeZone() } = {}) {
  const q = new URLSearchParams()
  q.set('asOf', rolling ? AS_OF_TODAY : asOf)
  if (rolling) q.set('tz', tz)
  if (earliest) q.set('earliest', earliest)
  return { method: 'GET', path: `/reports/exago/${String(exagoId).toLowerCase()}/run?${q.toString()}` }
}

/**
 * A `?jobId=` reopen's stored body → the report, exactly as `exagoReportService.run`
 * returns it (the job stored the run route's own answer). Refuses a shape it cannot read
 * rather than drawing an empty report from it.
 */
export function readSavedExagoResult(body) {
  const report = body?.data
  if (!report || !Array.isArray(report.attorneys) || !report.grandTotal || !report.parameters) {
    throw new Error('The saved result came back in a shape we could not read.')
  }
  return report
}

/**
 * Spec 209 §10: the kind of report a stored Exago job body holds — 'generic' when the run
 * route answered spec 209's generic shape, else 'reviewed' (spec 190's answer, which carries
 * no `kind`). Decides which page reads a `?jobId=` reopen; reads nothing else.
 */
export const savedExagoKind = (body) => (body?.data?.kind === 'generic' ? 'generic' : 'reviewed')

/**
 * Spec 209: a `?jobId=` reopen's stored body → the report, dispatched on the body's OWN
 * kind. A generic body must pass the same guard as a live generic run
 * (`readGenericExagoRun`); anything else is spec 190's reader, unchanged. Either refuses a
 * shape it cannot read rather than drawing an empty report from it.
 */
export function readSavedExagoRun(body) {
  if (savedExagoKind(body) === 'generic') {
    try {
      return readGenericExagoRun(body.data)
    } catch {
      throw new Error('The saved result came back in a shape we could not read.')
    }
  }
  return readSavedExagoResult(body)
}

/** What the schedule modal says about a recurring run's dates (it replaces the generic sentence). */
export function exagoScheduleDatesNote({ asOf } = {}, today) {
  if (rollsWithRun({ asOf }, today)) {
    return 'Every run is as of the day it runs, in your time zone. The earliest date stays where you put it.'
  }
  return asOf
    ? `Every run is as of ${fmtDisplayDate(asOf)}, the date shown above. Set As Of to today for a run that is as of the day it runs.`
    : undefined
}
